import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { wsClient } from '../../services/websocket-client.js';
import * as executionApi from '../../services/execution-api.js';

@customElement('cp-plan-review-modal')
export class CpPlanReviewModal extends LitElement {
  createRenderRoot() { return this; }

  @state() private _ticketId = '';
  @state() private _open = false;
  @state() private _planContent = '';
  @state() private _submitting = false;
  @state() private _action: 'approve' | 'reject' | null = null;

  private _wsHandler: ((e: Event) => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload: Record<string, unknown> }>).detail;
      if (msg.type !== 'execution:plan-ready') return;
      const ticketId = msg.payload?.ticketId as string | undefined;
      if (!ticketId) return;
      this._ticketId = ticketId;
      this._planContent = (msg.payload?.planContent as string) ?? '';
      this._open = true;
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  private _renderMarkdown(text: string) {
    const rawHtml = marked.parse(text, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private async _onApprove(): Promise<void> {
    if (this._submitting || !this._ticketId) return;
    this._submitting = true;
    this._action = 'approve';
    try {
      await executionApi.approvePlan(this._ticketId);
      this._close();
    } catch {
      // Keep modal open so user can retry
    } finally {
      this._submitting = false;
      this._action = null;
    }
  }

  private async _onReject(): Promise<void> {
    if (this._submitting || !this._ticketId) return;
    this._submitting = true;
    this._action = 'reject';
    try {
      await executionApi.rejectPlan(this._ticketId);
      this._close();
    } catch {
      // Keep modal open so user can retry
    } finally {
      this._submitting = false;
      this._action = null;
    }
  }

  private _close(): void {
    this._open = false;
    this._planContent = '';
    this._ticketId = '';
  }

  private _onBackdropOrCloseClick(): void {
    void this._onReject();
  }

  render() {
    if (!this._open) return nothing;

    return html`
      <div class="cp-plan-modal__backdrop" @click=${this._onBackdropOrCloseClick}>
        <div class="cp-plan-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="cp-plan-modal__header">
            <span class="cp-plan-modal__icon">≡</span>
            <span class="cp-plan-modal__title">Plan-Review</span>
            <button class="cp-plan-modal__close" @click=${this._onBackdropOrCloseClick} title="Ablehnen & Schliessen">×</button>
          </div>
          <div class="cp-plan-modal__body">
            <div class="cp-plan-modal__content">
              ${this._renderMarkdown(this._planContent)}
            </div>
          </div>
          <div class="cp-plan-modal__footer">
            <button
              class="cp-plan-modal__reject-btn"
              ?disabled=${this._submitting}
              @click=${this._onReject}
            >${this._action === 'reject' ? 'Wird abgelehnt...' : 'Ablehnen'}</button>
            <button
              class="cp-plan-modal__approve-btn"
              ?disabled=${this._submitting}
              @click=${this._onApprove}
            >${this._action === 'approve' ? 'Wird gestartet...' : 'Genehmigen & Ausfuehren'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-plan-review-modal': CpPlanReviewModal;
  }
}
